import { errorView } from '@krp/client-runtime'

import type { AqsiDispatch } from '../../../shared/model/dispatch'
import { aqsiRegisterLink, aqsiRegisterLinkCheck } from '../api/rmkApi'
import { AQSI_LINK_ADVICE } from './healthTexts'
import type { AqsiRegisterLink } from './types'

/** Перед какой командой проверяется касса: от этого зависят слова «что не случилось». */
export type ProbeFor = 'shift' | 'payment'

/**
 * Итог проверки перед командой кассе: можно ли отправлять. Не ответила касса — команда НЕ отправляется;
 * «не проверяется», «ответ не подтверждён» и «на связи» команду не останавливают (AQSI бывает отвечает `Error`
 * и у включённой кассы — тогда решает сама касса). Кабинет недоступен — стоп: команда всё равно не дойдёт.
 * Совет — из словаря связи по коду причины (`healthTexts.ts`), как и в мини-окне точки «Связь».
 */
export function mayProceedAfterProbe(link: AqsiRegisterLink, what: ProbeFor): { proceed: boolean; message: string | null } {
  if (link.server === 'error') {
    return {
      proceed: false,
      message:
        `Кабинет AQSI не отвечает${link.serverReason !== null ? `: ${link.serverReason}` : ''}. ` +
        (what === 'payment' ? 'Оплата не начиналась, деньги не списаны. Корзина сохранена.' : 'Команда кассе не отправлена.'),
    }
  }
  if (link.device === 'offline') {
    const advice = AQSI_LINK_ADVICE['no-answer']
    const reason = link.reason !== null ? ` Ответ службы: ${link.reason}` : ''
    return {
      proceed: false,
      message:
        what === 'payment'
          ? `Касса не ответила. Деньги не списаны, чек не пробит: оплата не начиналась. ${advice} Покупатель может подождать или оплатить позже — корзина сохранена.${reason}`
          : `Касса не ответила за срок проверки: команда не отправлена. ${advice}${reason}`,
    }
  }
  return { proceed: true, message: link.device === 'unknown' ? link.reason : null }
}

/** Итог проверки связи перед командой кассе (UI-RMK-FLOW 3.9): можно ли отправлять. */
export interface ProbeGate {
  proceed: boolean
  /** Слова для человека: почему команда не отправлена, либо замечание службы рядом с продолжением. */
  message: string | null
}

/**
 * Проверка кассы ПЕРЕД командой, которая ей что-то отправит (открыть смену, оплатить): только по явному
 * действию человека и только если проверка аппарата включена на службе и «на связи» не подтверждено.
 * Чтение связи кассе ничего не шлёт; проверка — одна служебная операция (чтение ФН), не чаще раза в 30 с
 * на кассу (служба вернёт прошлый итог). Не ответила — команда НЕ отправляется. Не удалось ни прочитать, ни
 * проверить — решает сама служба при команде (её отказ человек увидит словами). В цикле не вызывается.
 */
export async function probeRegister(
  dispatch: AqsiDispatch,
  resourceId: string,
  deviceId: number,
  what: ProbeFor,
): Promise<ProbeGate> {
  const read = await dispatch(aqsiRegisterLink.initiate({ resourceId, deviceId }, { forceRefetch: true, subscribe: false }))
  const link = read.data
  if (link === undefined || !link.probeEnabled || link.device === 'online') return { proceed: true, message: null }
  try {
    const checked = await dispatch(aqsiRegisterLinkCheck.initiate({ resourceId, deviceId }, { track: false })).unwrap()
    return mayProceedAfterProbe(checked, what)
  } catch (error) {
    const view = errorView(error)
    return {
      proceed: false,
      message:
        `Проверить кассу не удалось: ${view.message}. ` +
        (what === 'payment' ? 'Оплата не начиналась, деньги не списаны. Корзина сохранена.' : 'Команда кассе не отправлена.'),
    }
  }
}
